import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {AreaKey, LevelType, SelectedLevel} from 'app/original-models/level-type.model';
import {ChartType, ChartViewType} from 'app/pages/planning/common';
import {PeriodType} from 'app/original-models/period-type.model';
import {ColorThemes, DefaultColorTheme} from "../models/profile/color-theme.model";
import {BillToken} from 'app/models/monite/bill-token.model';

@Injectable({ providedIn: 'root' })
export class StorageService {
    readonly accessTokenKey = 'accessToken';
    readonly mfaTokenKey = 'mfaToken';
    readonly refreshTokenKey = 'refreshToken';
    readonly requestTokenSentKey = 'requestTokenSent';
    readonly rememberMeKey = 'rememberMe';
    readonly lastActivityKey = 'lastActivity';
    readonly currentUserIdKey = 'currentUserId';
    readonly currentUserModel = 'currentUserModel';
    readonly currentUserEmail = 'currentUserEmail';
    readonly currentUserName = 'currentUserName';
    readonly currentUserFirstName = 'currentUserFirstName';
    readonly currentCompanyIdKey = 'currentCompanyId';
    readonly currentLevelKey = 'currentLevel';
    readonly currentCompaniesIdKey = 'currentCompaniesId';
    readonly mainMenuStateKey = 'mainMenuState';
    readonly menuClickedKey = 'menuClicked';
    readonly pageLimitKey = 'pageLimit';
    readonly todayOnlyKey = 'todayOnly';
    readonly advancedSubscriptionKey = 'advancedSubscription';
    readonly defaultsLoadedKey = 'defaultsLoaded';
    readonly periodTypeKey = 'periodType';
    readonly chartTypeKey = 'chartType';
    readonly chartViewTypeKey = 'chartViewType';
    readonly languageKey = 'language';
    readonly startingPageKey = 'startingPage';
    readonly appVersion = 'version';
    readonly sortingKey = 'sorting';
    readonly sortingOrderKey = 'sortingOrder';
    readonly sortingPropertNameKey = 'sortingPropertName';
    readonly searchKeywordsKey = 'searchKeywords';
    readonly colorThemeTypeKey = 'colorThemeType';
    readonly billsPageEnabledKey = 'billsPageEnabled';
    readonly bankingServiceStatusKey = 'bankingServiceStatus';
    readonly showSocketDataInConsoleKey = 'showSocketDataInConsole';
    readonly billTokenKey = 'billToken';
    private _localItemSubject = new BehaviorSubject<any>(null);
    private _windowResizedSubject = new BehaviorSubject<any>(null);
    _isDarkMode = new BehaviorSubject<any>(null);
    _isMenuDefaultCollapse = new BehaviorSubject<any>(null);
    _isMenuSwitched = new BehaviorSubject<any>(null);
    _isBillsPageEnabled = new BehaviorSubject<any>(null);
    bankingServiceStatus$ = new BehaviorSubject<string | null>(null);
    isMenuSetupMode$ = new BehaviorSubject<boolean>(false);
    public prevState?: any;
    constructor() {
        window.addEventListener('storage', (e) => {
            if (e.key === 'currentUserId' && e.oldValue) {
                this._localItemSubject.next({processLogout: true});
            } else if (e.key === this.refreshTokenKey && e.newValue) {
                this._localItemSubject.next({refreshToken: true});
            }
        });
    }

    getStorageItem(key: string) {
        return this.rememberMe ? this.getLocalItem(key) : this.getSessionItem(key);
    }

    setStorageItem(key: string, value: any) {
        return this.rememberMe ? this._setItemLocal(key, value) : this._setItemSession(key, value);
    }

    removeStorageItem(key: string) {
        return this.rememberMe ? this.removeLocalItem(key) : this.removeSessionItem(key);
    }

    clearAll() {
        let mainMenuState = this.getStorageItem('mainMenuState');
        let showSocketDataInConsole = this.showSocketDataInConsole;
        // Remove currentUserId explicitly so other tabs receive a 'storage' event
        // with key='currentUserId'. localStorage.clear() below fires the event with
        // key=null, which the cross-tab listener (constructor above) ignores.
        localStorage.removeItem(this.currentUserIdKey);
        sessionStorage.removeItem(this.currentUserIdKey);
        if (sessionStorage) {
            sessionStorage.clear();
        }
        if (localStorage) {
            localStorage.clear();
        }
        this.setStorageItem('mainMenuState', mainMenuState);
        this.showSocketDataInConsole = showSocketDataInConsole;
        this._localItemSubject.next({clearAll: true});
    }

    private _setItemLocal(key: string, value: any) {
        return (value === null) ? this.removeLocalItem(key) : this.setLocalItem(key, value);
    }

    private _setItemSession(key: string, value: any) {
        return (value === null) ? this.removeSessionItem(key) : this.setSessionItem(key, value);
    }

    get rememberMe(): boolean {
        return this.getLocalItem(this.rememberMeKey) === 'true';
    }

    set rememberMe(value: boolean) {
        value ? this.setLocalItem(this.rememberMeKey, String(value)) : this.removeLocalItem(this.rememberMeKey);
    }

    get menuClicked(): boolean {
        return this.getStorageItem(this.menuClickedKey) === 'true';
    }

    set menuClicked(value: boolean) {
        value ? this.setStorageItem(this.menuClickedKey, String(value)) : this.removeStorageItem(this.menuClickedKey);
    }

    get lastActivity(): number {
        return Number(this.getLocalItem(this.lastActivityKey));
    }

    set lastActivity(value: number) {
        this.setLocalItem(this.lastActivityKey, value ? String(value) : '');
    }

    get mfaToken(): string {
        return this.getStorageItem(this.mfaTokenKey);
    }

    set mfaToken(value: string) {
        this.setStorageItem(this.mfaTokenKey, value);
    }

    get accessToken(): string {
        return this.getLocalItem(this.accessTokenKey);
    }

    set accessToken(value: string) {
        this.setLocalItem(this.accessTokenKey, value);
        this._localItemSubject.next({accessToken: value});
    }

    get refreshToken(): string {
        return this.getLocalItem(this.refreshTokenKey);
    }

    set refreshToken(value: string) {
        this.setLocalItem(this.refreshTokenKey, value);
    }

    get requestTokenSent(): string {
        return this.getLocalItem(this.requestTokenSentKey);
    }

    set requestTokenSent(value: string) {
        value ? this.setLocalItem(this.requestTokenSentKey, String(value)) : this.removeLocalItem(this.requestTokenSentKey);
    }

    get mainMenuState(): string {
        return this.getStorageItem(this.mainMenuStateKey);
    }

    set mainMenuState(value: string) {
        if (value !== this.mainMenuState) {
            this.setStorageItem(this.mainMenuStateKey, value);
            this._windowResizedSubject.next(true);
        }
    }

    get userId(): number {
        return Number(this.getStorageItem(this.currentUserIdKey));
    }

    set userId(value: number) {
        let strValue = value === null ? null : String(value);
        this.setStorageItem(this.currentUserIdKey, strValue);
    }

    get userEmail(): string {
        return this.getStorageItem(this.currentUserEmail);
    }

    set userEmail(value: string) {
        this.setStorageItem(this.currentUserEmail, value);
    }

    get model(): string {
        return this.getStorageItem(this.currentUserModel);
    }

    set model(value: string) {
        this.setStorageItem(this.currentUserModel, value);
    }

    get companyId(): number {
        return Number(this.getStorageItem(this.currentCompanyIdKey));
    }

    set companyId(value: number) {
        let strValue = value === null ? null : String(value);
        this.setStorageItem(this.currentCompanyIdKey, strValue);
    }

    get selectedLevel(): SelectedLevel {
        return JSON.parse(this.getStorageItem(this.currentLevelKey));
    }

    set selectedLevel(value: SelectedLevel) {
        let strValue = value === null ? null : JSON.stringify(value);
        this.setStorageItem(this.currentLevelKey, strValue);
        let commonData = this.getLocalItem(this.currentLevelKey);
        if (strValue && (!commonData || commonData !== strValue)) {
            this.copyStorageAuthData(false)
        }
    }

    get companiesId(): number[] {
        let value = this.getStorageItem(this.currentCompaniesIdKey);
        let result = value == null ? [] : value.split(',').map((e) => parseInt(e));
        return result;
    }

    set companiesId(value: number[]) {
        let strValue = value === null || value.length === 0 ? null : String(value.join(','));
        this.setStorageItem(this.currentCompaniesIdKey, strValue);
    }

    get name(): string {
        return this.getStorageItem(this.currentUserName);
    }

    set name(value: string) {
        this.setStorageItem(this.currentUserName, value);
    }

    get firstName(): string {
        return this.getStorageItem(this.currentUserFirstName);
    }

    set firstName(value: string) {
        this.setStorageItem(this.currentUserFirstName, value);
    }

    get pageLimit(): number {
        let value = this.getStorageItem(this.pageLimitKey);
        let result = !value ? undefined : parseInt(value);
        return result;
    }

    set pageLimit(value: number) {
        let strValue = !value ? undefined : String(value);
        this.setStorageItem(this.pageLimitKey, strValue);
    }

    get todayOnly(): boolean {
        return this.getStorageItem(this.todayOnlyKey) === 'true';
    }

    set todayOnly(value: boolean) {
        this.setStorageItem(this.todayOnlyKey, value);
        value ? this.setStorageItem(this.todayOnlyKey, String(value)) : this.removeStorageItem(this.todayOnlyKey);
    }

    get advancedSubscription(): boolean {
        return this.getStorageItem(this.advancedSubscriptionKey) === 'true';
    }

    set advancedSubscription(value: boolean) {
        this.setStorageItem(this.advancedSubscriptionKey, value);
        value ? this.setStorageItem(this.advancedSubscriptionKey, String(value)) : this.removeStorageItem(this.advancedSubscriptionKey);
    }

    get defaultsLoaded(): boolean {
        return this.getLocalItem(this.defaultsLoadedKey) === 'true';
    }

    set defaultsLoaded(value: boolean) {
        value ? this.setLocalItem(this.defaultsLoadedKey, String(value)) : this.removeLocalItem(this.defaultsLoadedKey);
    }

    get periodType(): PeriodType {
        return JSON.parse(this.getStorageItem(this.periodTypeKey));
    }

    set periodType(value: PeriodType) {
        let strValue = value === null ? null : JSON.stringify(value);
        this.setStorageItem(this.periodTypeKey, strValue);
    }

    get chartType(): ChartType {
        return JSON.parse(this.getStorageItem(this.chartTypeKey));
    }

    set chartType(value: ChartType) {
        let strValue = value === null ? null : JSON.stringify(value);
        this.setStorageItem(this.chartTypeKey, strValue);
    }

    get chartViewType(): ChartViewType {
        return JSON.parse(this.getStorageItem(this.chartViewTypeKey));
    }

    set chartViewType(value: ChartViewType) {
        let strValue = value === null ? null : JSON.stringify(value);
        this.setStorageItem(this.chartViewTypeKey, strValue);
    }

    get language(): string {
        return this.getStorageItem(this.languageKey);
    }

    set language(value: string) {
        this.setStorageItem(this.languageKey, value);
    }

    get startingPage(): string {
        return this.getStorageItem(this.startingPageKey);
    }

    set startingPage(value: string) {
        this.setStorageItem(this.startingPageKey, value);
    }

    get version(): string {
        return this.getStorageItem(this.appVersion);
    }

    set version(value: string) {
        this.setStorageItem(this.appVersion, value);
    }

    get searchKeywords(): string[] {
        let value = this.getStorageItem(this.searchKeywordsKey);
        let result = value == null ? [] : value.split(',');
        return result;
    }

    set searchKeywords(value: string[]) {
        let strValue = value === null || value.length === 0 ? null : String(value.join(','));
        this.setStorageItem(this.searchKeywordsKey, strValue);
    }

    get colorTheme(): ColorThemes {
        return (localStorage.getItem(this.colorThemeTypeKey) || DefaultColorTheme) as ColorThemes;
    }

    set colorTheme(value: string) {
        localStorage.setItem(this.colorThemeTypeKey, value);
    }

    get billsPageEnabled(): boolean | null {
        return this.getStorageItem(this.billsPageEnabledKey) ? this.getStorageItem(this.billsPageEnabledKey) === 'true' : null;
    }

    set billsPageEnabled(value: boolean | null) {
        this.setStorageItem(this.billsPageEnabledKey, value);
        value ? this.setStorageItem(this.billsPageEnabledKey, String(value)) : this.removeStorageItem(this.billsPageEnabledKey);

        this._isBillsPageEnabled.next(value);
    }

    get bankingServiceStatus(): string | null {
        return this.getStorageItem(this.bankingServiceStatusKey);
    }

    set bankingServiceStatus(value: string | null) {
        this.setStorageItem(this.bankingServiceStatusKey, value);
        value ? this.setStorageItem(this.bankingServiceStatusKey, String(value)) : this.removeStorageItem(this.bankingServiceStatusKey);
        this.bankingServiceStatus$.next(value);
    }

    get showSocketDataInConsole(): boolean {
        return this.getLocalItem(this.showSocketDataInConsoleKey) === 'true';
    }

    set showSocketDataInConsole(value: boolean) {
        value ? this.setLocalItem(this.showSocketDataInConsoleKey, String(value)) : this.removeLocalItem(this.showSocketDataInConsoleKey);
    }

    get billToken(): BillToken {
        const token = this.getLocalItem(this.billTokenKey);
        return token ? JSON.parse(token) : null;
    }

    set billToken(value: BillToken) {
        value ? this.setLocalItem(this.billTokenKey,  JSON.stringify(value)) : this.removeLocalItem(this.billTokenKey);
    }

    getSorting(target: 'invoices' | 'transactions' | 'budgets' | 'bills' = 'invoices'): any {
        let value = this.getStorageItem(`${target}_${this.sortingKey}`);
        let result = !value ? undefined : JSON.parse(value);
        return result;
    }

    setSorting(target: 'invoices' | 'transactions' | 'budgets' | 'bills' = 'invoices', value) {
        let strValue = value === null ? null : JSON.stringify(value);
        this.setStorageItem(`${target}_${this.sortingKey}`, strValue);
    }

    private getLocalItem(key: string): string | null {
        return localStorage.getItem(key);
    }

    private removeLocalItem(key: string): void {
        localStorage.removeItem(key);
    }

    private setLocalItem(key: string, data: string): void {
        localStorage.setItem(key, data);
        let resp = {};
        resp[key] = data;
        this._localItemSubject.next(resp);
    }

    private getSessionItem(key: string): string | null {
        return sessionStorage.getItem(key);
    }

    private removeSessionItem(key: string): void {
        sessionStorage.removeItem(key);
    }

    private setSessionItem(key: string, data: string): void {
        sessionStorage.setItem(key, data);
        let resp = {};
        resp[key] = data;
        this._localItemSubject.next(resp);
    }

    localItemChanged(): Observable<any> {
        return this._localItemSubject.asObservable();
    }

    windowResized(): Observable<any> {
        return this._windowResizedSubject.asObservable();
    }

    triggerWindowResize() {
        this._windowResizedSubject.next(true);
    }

    addCompany(id) {
        if (this.companiesId.indexOf(id) < 0) {
            let companies = this.companiesId;
            companies.push(id);
            this.companiesId = companies;
        }
    }

    //to perform requests related to group or companies selected
    getArea() {
        return {
            key: this.selectedLevel && this.selectedLevel.type === LevelType.Group ? AreaKey.Groups : AreaKey.Companies,
            id: this.selectedLevel && this.selectedLevel.id ? this.selectedLevel.id : this.companyId
        };
    }

    triggerDarkMode(isDarkMode: boolean) {
        this.setStorageItem('isDarkMode', isDarkMode);
        this._isDarkMode.next(isDarkMode);
    }

    triggerMenuStateDefault(state: boolean) {
        this.setStorageItem('isMenuDefaultCollapse', state);
        if (state) {
            this.setStorageItem('mainMenuState', 'is-closed');
        } else {
            this.setStorageItem('mainMenuState', 'is-opened');
        }
        this._isMenuDefaultCollapse.next(state);
    }

    triggerMenuSwitch() {
        this._isMenuSwitched.next(true);
    }

    needCopyStorageAuthData() {
        return localStorage.getItem(this.currentLevelKey) && !sessionStorage.getItem(this.currentLevelKey);
    }
    copyStorageAuthData(fromLocalStorage: boolean) {
        let source = fromLocalStorage ? localStorage : sessionStorage;
        let target = fromLocalStorage ? sessionStorage : localStorage;
        let keysToCopy = [//this.accessTokenKey,
            //this.refreshTokenKey,
            this.currentUserIdKey,
            this.currentUserModel,
            this.currentUserEmail,
            this.currentUserName,
            this.currentCompaniesIdKey,
            this.currentCompanyIdKey,
            this.currentLevelKey,
            this.appVersion
        ];
        keysToCopy.forEach(key => {
            target.setItem(key, source.getItem(key));
        })
    }
}
